import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Share, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'pwa-install-dismissed';

function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  return /iP(hone|od|ad)/.test(ua) && /WebKit/.test(ua) && !/(CriOS|FxiOS|OPiOS|EdgiOS)/.test(ua);
}

function isInStandaloneMode(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || ('standalone' in navigator && (navigator as any).standalone === true);
}

export default function PwaInstallPrompt() {
  const { t } = useTranslation('admin');
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [mode, setMode] = useState<'chromium' | 'ios' | null>(null);

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY)) return;
    if (isInStandaloneMode()) return;

    // Chromium browsers (Android Chrome, Edge, Samsung Internet, etc.)
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setMode('chromium');
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari — no beforeinstallprompt, detect manually
    if (isIosSafari()) {
      setMode('ios');
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setMode(null);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setMode(null);
  };

  if (!mode) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto max-w-md rounded-xl border bg-card p-4 shadow-lg flex items-center gap-3">
        <div className="flex-shrink-0 rounded-lg bg-primary/10 p-2">
          {mode === 'ios'
            ? <Share className="h-5 w-5 text-primary" />
            : <Download className="h-5 w-5 text-primary" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            {t('pwa.installTitle', 'Install App')}
          </p>
          <p className="text-xs text-muted-foreground">
            {mode === 'ios'
              ? t('pwa.iosInstallDescription', 'Tap the share button, then "Add to Home Screen"')
              : t('pwa.installDescription', 'Add to your home screen for quick access')
            }
          </p>
        </div>
        {mode === 'chromium' && (
          <button
            onClick={handleInstall}
            className="flex-shrink-0 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {t('pwa.install', 'Install')}
          </button>
        )}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={t('common:buttons.close', 'Close')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
